import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';
import BackButton from '../common/BackButton';
import KycProgressBar from '../KycProgressBar';
import API from '../../api';

const IdCardGuide = ({ side }) => (
  <svg viewBox="0 0 340 215" style={{ width: '100%', maxWidth: '320px', display: 'block', margin: '0 auto' }}>
    {/* dashed outline */}
    <rect x="10" y="10" width="320" height="195" rx="12" fill="none"
      stroke={SEMANTIC.ctaPrimary} strokeWidth="2" strokeDasharray="8,4" opacity="0.45" />
    {/* corner markers */}
    <path d="M10,32 L10,10 L32,10" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    <path d="M308,10 L330,10 L330,32" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    <path d="M10,183 L10,205 L32,205" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    <path d="M308,205 L330,205 L330,183" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    {side === 'front' ? (
      <>
        {/* photo placeholder */}
        <rect x="24" y="32" width="70" height="90" rx="5"
          fill="none" stroke={SEMANTIC.textSecondary} strokeWidth="1.5" opacity="0.4" />
        <text x="59" y="83" textAnchor="middle" fill={SEMANTIC.textSecondary} fontSize="11" opacity="0.5">foto</text>
        {/* data lines */}
        <line x1="108" y1="47" x2="308" y2="47" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        <line x1="108" y1="63" x2="270" y2="63" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        <line x1="108" y1="79" x2="285" y2="79" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        <line x1="108" y1="95" x2="255" y2="95" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        {/* MRZ lines */}
        <rect x="24" y="155" width="292" height="11" rx="2"
          fill={SEMANTIC.textSecondary} opacity="0.1" />
        <rect x="24" y="172" width="292" height="11" rx="2"
          fill={SEMANTIC.textSecondary} opacity="0.1" />
        <text x="170" y="136" textAnchor="middle" fill={SEMANTIC.ctaPrimary} fontSize="14" fontWeight="bold" opacity="0.6">FRONTE</text>
      </>
    ) : (
      <>
        {/* chip */}
        <rect x="24" y="45" width="52" height="40" rx="7"
          fill="none" stroke={SEMANTIC.textSecondary} strokeWidth="1.5" opacity="0.4" />
        <line x1="50" y1="45" x2="50" y2="85" stroke={SEMANTIC.textSecondary} strokeWidth="1" opacity="0.3" />
        <line x1="24" y1="65" x2="76" y2="65" stroke={SEMANTIC.textSecondary} strokeWidth="1" opacity="0.3" />
        {/* data lines */}
        <line x1="90" y1="47" x2="308" y2="47" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        <line x1="90" y1="63" x2="280" y2="63" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        <line x1="90" y1="79" x2="290" y2="79" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        <line x1="90" y1="95" x2="260" y2="95" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
        {/* signature / barcode area */}
        <rect x="24" y="130" width="180" height="40" rx="3"
          fill={SEMANTIC.textSecondary} opacity="0.08" />
        <text x="220" y="158" textAnchor="middle" fill={SEMANTIC.ctaPrimary} fontSize="14" fontWeight="bold" opacity="0.6">RETRO</text>
      </>
    )}
  </svg>
);

const PassportGuide = () => (
  <svg viewBox="0 0 220 310" style={{ width: '100%', maxWidth: '180px', display: 'block', margin: '0 auto' }}>
    {/* dashed outline */}
    <rect x="10" y="10" width="200" height="290" rx="8"
      fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="2" strokeDasharray="8,4" opacity="0.45" />
    {/* corner markers */}
    <path d="M10,32 L10,10 L32,10" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    <path d="M188,10 L210,10 L210,32" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    <path d="M10,278 L10,300 L32,300" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    <path d="M188,300 L210,300 L210,278" fill="none" stroke={SEMANTIC.ctaPrimary} strokeWidth="3" />
    {/* photo placeholder */}
    <rect x="22" y="28" width="65" height="82" rx="4"
      fill="none" stroke={SEMANTIC.textSecondary} strokeWidth="1.5" opacity="0.4" />
    <text x="55" y="74" textAnchor="middle" fill={SEMANTIC.textSecondary} fontSize="11" opacity="0.5">foto</text>
    {/* data lines */}
    <line x1="100" y1="38" x2="198" y2="38" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    <line x1="100" y1="53" x2="185" y2="53" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    <line x1="100" y1="68" x2="190" y2="68" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    <line x1="100" y1="83" x2="180" y2="83" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    {/* more data lines */}
    <line x1="22" y1="125" x2="198" y2="125" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    <line x1="22" y1="140" x2="198" y2="140" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    <line x1="22" y1="155" x2="198" y2="155" stroke={SEMANTIC.textSecondary} strokeWidth="2" opacity="0.25" />
    {/* MRZ area */}
    <rect x="22" y="235" width="176" height="14" rx="2" fill={SEMANTIC.textSecondary} opacity="0.1" />
    <rect x="22" y="255" width="176" height="14" rx="2" fill={SEMANTIC.textSecondary} opacity="0.1" />
    <rect x="22" y="275" width="176" height="14" rx="2" fill={SEMANTIC.textSecondary} opacity="0.1" />
    {/* label */}
    <text x="110" y="208" textAnchor="middle" fill={SEMANTIC.ctaPrimary} fontSize="12" fontWeight="bold" opacity="0.6">Pagina dati</text>
    <text x="110" y="224" textAnchor="middle" fill={SEMANTIC.ctaPrimary} fontSize="12" fontWeight="bold" opacity="0.6">anagrafici</text>
  </svg>
);

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const DocumentUpload = () => {
  const [docType, setDocType] = useState('identity_card');
  const [uploadStep, setUploadStep] = useState('front'); // 'front' | 'back'
  const [frontFile, setFrontFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const isIdCard = docType === 'identity_card';
  const currentPreview = uploadStep === 'front' ? frontPreview : backPreview;

  const handleDocTypeChange = (type) => {
    setDocType(type);
    setUploadStep('front');
    setFrontFile(null); setFrontPreview(null);
    setBackFile(null); setBackPreview(null);
    setError('');
  };

  const validateFile = (file) => {
    if (file.size > 5 * 1024 * 1024) { setError(t('kyc.document_upload.error_size')); return false; }
    if (!file.type.startsWith('image/')) { setError(t('kyc.document_upload.error_type')); return false; }
    return true;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFile(file)) return;
    setError('');
    if (uploadStep === 'front') {
      setFrontFile(file);
      setFrontPreview(URL.createObjectURL(file));
    } else {
      setBackFile(file);
      setBackPreview(URL.createObjectURL(file));
    }
  };

  const handleContinueToBack = () => {
    setUploadStep('back');
    setError('');
  };

  const handleBackToPrev = () => {
    if (uploadStep === 'back') {
      setUploadStep('front');
      setBackFile(null); setBackPreview(null);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!frontFile) { setError(t('kyc.document_upload.error_no_file')); return; }
    if (isIdCard && !backFile) { setError(t('kyc.document_upload.error_no_back')); return; }
    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      const formData = new FormData();
      formData.append('document', frontFile);
      if (isIdCard) formData.append('document_back', backFile);
      formData.append('user_id', userId);
      formData.append('doc_type', docType);
      await API.post('/kyc/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      navigate('/kyc/membership');
    } catch (err) {
      setError(t('kyc.document_upload.error_upload'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const docTypeButtonStyle = (active) => ({
    flex: 1,
    padding: isMobile ? '8px 6px' : `${UI.spacing.md} ${UI.spacing.sm}`,
    backgroundColor: active ? SEMANTIC.ctaPrimary : 'transparent',
    color: active ? SEMANTIC.ctaPrimaryText : SEMANTIC.textPrimary,
    border: `2px solid ${active ? SEMANTIC.ctaPrimary : 'rgba(255,255,255,0.15)'}`,
    borderRadius: UI.borderRadius.sm,
    cursor: 'pointer',
    fontSize: UI.fontSize.sm,
    fontWeight: UI.fontWeight.bold,
    textAlign: 'center',
    transition: 'all 0.2s',
  });

  const isReadyToUpload = isIdCard ? (frontFile && backFile) : !!frontFile;
  const isReadyToContinue = isIdCard && uploadStep === 'front' && !!frontFile;

  const stepLabel = isIdCard
    ? (uploadStep === 'front'
        ? t('kyc.document_upload.step_front')
        : t('kyc.document_upload.step_back'))
    : null;

  const hint = isIdCard
    ? (uploadStep === 'front'
        ? t('kyc.document_upload.hint_front')
        : t('kyc.document_upload.hint_back'))
    : t('kyc.document_upload.hint_passport');

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: isMobile ? 8 : UI.spacing.lg, paddingBottom: uploading ? 90 : (isMobile ? 8 : UI.spacing.lg) }}>
      <BackButton to={uploadStep === 'back' ? undefined : '/kyc/personal-data'} onClick={uploadStep === 'back' ? handleBackToPrev : undefined} />
      <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 8 : UI.spacing.xl }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto Logo" style={{ width: isMobile ? 70 : 120 }} />
        </div>

        <KycProgressBar currentStep={4} />

        <div style={{ backgroundColor: SEMANTIC.backgroundCard, borderRadius: UI.borderRadius.md, padding: isMobile ? 12 : UI.spacing.xl, boxShadow: UI.shadow.card }}>
          <h2 style={{ color: SEMANTIC.actionBuy, marginBottom: isMobile ? 8 : UI.spacing.md, fontSize: isMobile ? '18px' : UI.fontSize.title, fontWeight: UI.fontWeight.bold, textAlign: 'center' }}>
            {t('kyc.document_upload.title')}
          </h2>

          {error && (
            <div style={{ backgroundColor: 'rgba(255, 77, 77, 0.1)', border: `2px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.sm, marginBottom: isMobile ? 8 : UI.spacing.lg, color: SEMANTIC.actionSell, textAlign: 'center', fontSize: UI.fontSize.sm }}>
              {error}
            </div>
          )}

          {/* Selezione tipo documento */}
          <div style={{ marginBottom: isMobile ? 10 : UI.spacing.xl }}>
            <label style={{ color: SEMANTIC.textPrimary, display: 'block', marginBottom: isMobile ? 6 : UI.spacing.sm, fontSize: UI.fontSize.sm, fontWeight: UI.fontWeight.bold }}>
              {t('kyc.document_upload.doc_type_label')}
            </label>
            <div style={{ display: 'flex', gap: isMobile ? 8 : UI.spacing.md }}>
              <button style={docTypeButtonStyle(isIdCard)} onClick={() => handleDocTypeChange('identity_card')}>
                {t('kyc.document_upload.doc_type_id')}
              </button>
              <button style={docTypeButtonStyle(!isIdCard)} onClick={() => handleDocTypeChange('passport')}>
                {t('kyc.document_upload.doc_type_passport')}
              </button>
            </div>
          </div>

          {/* Step indicator (solo per CIE) */}
          {isIdCard && (
            <div style={{ display: 'flex', alignItems: 'center', gap: UI.spacing.sm, marginBottom: isMobile ? 10 : UI.spacing.lg }}>
              <div style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: frontFile ? SEMANTIC.ctaPrimary : 'rgba(255,255,255,0.15)' }} />
              <div style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: backFile ? SEMANTIC.ctaPrimary : 'rgba(255,255,255,0.15)' }} />
              <span style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, whiteSpace: 'nowrap' }}>{stepLabel}</span>
            </div>
          )}

          {/* Guida visiva */}
          <div style={{ marginBottom: isMobile ? 10 : UI.spacing.lg, padding: isMobile ? 8 : UI.spacing.lg, backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: UI.borderRadius.md }}>
            {isIdCard
              ? <IdCardGuide side={uploadStep} />
              : <PassportGuide />}
            <p style={{ color: SEMANTIC.textSecondary, fontSize: isMobile ? 11 : UI.fontSize.sm, textAlign: 'center', marginTop: isMobile ? 6 : UI.spacing.md, marginBottom: 0 }}>
              {hint}
            </p>
          </div>

          {/* Area upload / preview */}
          <div style={{ border: `2px dashed ${currentPreview ? SEMANTIC.ctaPrimary : SEMANTIC.textSecondary}`, borderRadius: UI.borderRadius.md, padding: isMobile ? 12 : UI.spacing.xl, textAlign: 'center', marginBottom: isMobile ? 10 : UI.spacing.lg, backgroundColor: SEMANTIC.backgroundCard, transition: 'border-color 0.2s', opacity: uploading ? 0.4 : 1, pointerEvents: uploading ? 'none' : 'auto' }}>
            {currentPreview ? (
              <div>
                <img src={currentPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: UI.borderRadius.sm, marginBottom: UI.spacing.md }} />
                <button
                  onClick={() => {
                    if (uploadStep === 'front') { setFrontFile(null); setFrontPreview(null); }
                    else { setBackFile(null); setBackPreview(null); }
                  }}
                  style={{ background: 'transparent', border: 'none', color: SEMANTIC.actionSell, fontSize: UI.fontSize.md, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {t('kyc.document_upload.remove')}
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '40px', marginBottom: UI.spacing.md }}>📄</div>
                <div style={{ display: 'flex', gap: UI.spacing.md, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <label htmlFor={`fileInput-${uploadStep}`} style={{ display: 'inline-block', padding: `${UI.spacing.md} ${UI.spacing.xl}`, backgroundColor: SEMANTIC.ctaPrimary, color: SEMANTIC.ctaPrimaryText, borderRadius: UI.borderRadius.sm, cursor: 'pointer', fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold }}>
                    {t('kyc.document_upload.choose_file')}
                  </label>
                  <input id={`fileInput-${uploadStep}`} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
                  <label htmlFor={`cameraInput-${uploadStep}`} style={{ display: 'inline-block', padding: `${UI.spacing.md} ${UI.spacing.xl}`, backgroundColor: 'transparent', color: SEMANTIC.textPrimary, border: `1px solid ${SEMANTIC.ctaPrimary}`, borderRadius: UI.borderRadius.sm, cursor: 'pointer', fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold }}>
                    📷 {t('kyc.document_upload.take_photo')}
                  </label>
                  <input id={`cameraInput-${uploadStep}`} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} />
                </div>
              </>
            )}
          </div>

          {/* Bottone azione */}
          {isReadyToContinue ? (
            <button onClick={handleContinueToBack} style={{ width: '100%', padding: UI.spacing.md, backgroundColor: SEMANTIC.ctaPrimary, color: SEMANTIC.ctaPrimaryText, border: 'none', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, cursor: 'pointer' }}>
              {t('kyc.document_upload.continue_to_back')}
            </button>
          ) : (
            <button onClick={handleUpload} disabled={!isReadyToUpload || uploading} style={{ width: '100%', padding: UI.spacing.md, backgroundColor: isReadyToUpload && !uploading ? SEMANTIC.ctaPrimary : SEMANTIC.backgroundDisabled, color: isReadyToUpload && !uploading ? SEMANTIC.ctaPrimaryText : SEMANTIC.textDisabled, border: 'none', borderRadius: UI.borderRadius.sm, fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold, cursor: isReadyToUpload && !uploading ? 'pointer' : 'not-allowed' }}>
              {uploading
                ? t('kyc.document_upload.uploading')
                : isIdCard
                  ? t('kyc.document_upload.upload_id_button')
                  : t('kyc.document_upload.upload_passport_button')}
            </button>
          )}
        </div>
      </div>

      {/* Overlay progresso fisso in basso — sempre visibile su mobile */}
      {uploading && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
          backgroundColor: SEMANTIC.backgroundCard,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          padding: '12px 20px 28px',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm }}>
              {t('kyc.document_upload.uploading')}
            </span>
            <span style={{ color: SEMANTIC.ctaPrimary, fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold }}>
              {uploadProgress}%
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${uploadProgress}%`,
              borderRadius: 4,
              backgroundColor: SEMANTIC.ctaPrimary,
              transition: 'width 0.2s ease',
            }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
